import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { DataService } from '../http/dataService';
import { Columns, IPage, Result } from '../interfaces/helpers';

import { ACTIONS, ConfigColumns, PageConfig } from '../interfaces/internal';
import { IModel } from '../interfaces/models';
import { mapResult, PossibleResult } from '../lib/utils';

export abstract class Page<T extends IModel> implements IPage<T> {
  public queryItemsLimit: number;
  protected columnsVisibility = new BehaviorSubject<Columns[]>([]);
  protected pageItems = new BehaviorSubject<T[]>([]);
  protected dataService: DataService;
  private maxCol: number;

  constructor(public url: string, private title: string, config: PageConfig) {
    this.dataService = new DataService(config.http);
    this.maxCol = config.maxCol;
    this.queryItemsLimit = config.queryItemsLimit;
  }

  get items(): Observable<T[]> {
    return this.pageItems.asObservable();
  }

  get actions(): string[] {
    return [ACTIONS.edit, ACTIONS.delete];
  }

  get columns(): Observable<Columns[]> {
    return this.columnsVisibility.asObservable();
  }

  getTitle(): string {
    return this.title;
  }

  initializeColumns(
    config: ConfigColumns<T> = {
      keys: {} as T,
      hiddenFormFields: [],
      hiddenTableFields: [],
      dropdownFields: [],
      editableFields: [],
    }
  ) {
    const {
      keys,
      dropdownFields,
      hiddenTableFields,
      hiddenFormFields,
      editableFields,
    } = config;
    const col: string[] = Object.keys(keys) as string[];

    const newCol: Columns[] = col
      .filter((c) => !c.startsWith('_') && !c.toLowerCase().includes('id'))
      .map((c, index) => ({
        field: c,
        visible: index < this.maxCol,
        type:
          dropdownFields && dropdownFields.includes(c)
            ? 'option'
            : this.getType(keys, c),
        hiddenFormField: hiddenFormFields
          ? hiddenFormFields.includes(c)
          : false,
        hiddenTableField: hiddenTableFields
          ? hiddenTableFields.includes(c)
          : false,
        isEditable: editableFields ? editableFields.includes(c) : false,
      }));
    this.columnsVisibility.next(newCol);
  }

  getType(keys: T, c: string) {
    const varType = typeof keys[c];

    if (keys[c] === 'raw' || keys[c] === 'link') {
      return keys[c];
    }

    switch (varType) {
      case 'object':
        const objType = Object.prototype.toString.call(keys[c]);

        switch (objType) {
          case '[object Date]':
            return 'date';
          case '[object Array]':
            return 'array';
          default:
            return 'object';
        }
      default:
        return varType;
    }
  }

  setColumns(columns: Columns[]): Observable<Columns[]> {
    return this.columns.pipe(
      take(1),
      switchMap((cols) => {
        if (!cols || cols.length <= 0) {
          return of(columns);
        } else {
          const newColumns = cols.map((col) => {
            const index = columns.findIndex((c) => c.field === col.field);
            col.visible = !columns[index]
              ? col.visible
              : columns[index].visible;

            return col;
          });

          return of(newColumns);
        }
      }),
      tap((cols) => {
        this.columnsVisibility.next(cols);
      })
    );
  }

  add(item: any): Observable<T> {
    let data: T = { ...item, id: null };

    return this.dataService.post<T>(this.url, data).pipe(
      switchMap((result) => {
        data = { ...result };

        return this.items;
      }),
      take(1),
      tap((items) => {
        this.pageItems.next(items.concat(data));
      }),
      map(() => data)
    );
  }

  addBulk(items, endpoint: string): Observable<T[]> {
    const url = this.buildUrl(endpoint);

    return this.dataService.post<T>(url, items).pipe(
      take(1),
      map(() => [])
    );
  }

  delete(id: string): Observable<T> {
    const url = this.buildUrl(id);

    return this.dataService.delete<T>(url).pipe(take(1));
  }

  public loadDataLazily<I>(queryParams?: any): Observable<Result<T>> {
    const url = `${this.url}/query`;
    return this.dataService.getDataByLazyLoading(url, queryParams).pipe(
      take(1),
      map((result) => mapResult<I, T>(result, this.createFromDTO))
    );
  }

  protected getItem<G>(
    id: string,
    queryString?,
    headers?,
    doNotEncode?
  ): Observable<G | { [key: string]: G }> {
    const url = this.buildUrl(id);
    return this.dataService.get<G>(url, queryString, headers, doNotEncode);
  }

  protected getItems<G>(
    queryString?,
    headers?,
    doNotEncode?
  ): Observable<Result<T>> {
    return this.dataService
      .get<PossibleResult<T>>(this.url, queryString, headers, doNotEncode)
      .pipe(
        take(1),
        map((result) => mapResult<T, T>(result, this.createFromDTO))
      );
  }

  protected getPlainUrl<G>(queryString, header): Observable<Result<T>> {
    const url = `${this.url}${queryString}`;
    return this.dataService.getPlainRequest<G>(url, header).pipe(
      take(1),
      map((result) => mapResult<T, T>(result, this.createFromDTO))
    );
  }

  protected getItemsPost<G>(params?, headers?): Observable<Result<T>> {
    const url = this.url;

    return this.dataService.post<PossibleResult<T>>(url, params, headers).pipe(
      take(1),
      map((result) => mapResult<T, T>(result, this.createFromDTO))
    );
  }

  protected updateItems(item: any): Observable<any> {
    const data = { ...item };

    const url = this.buildUrl(item.id);
    return this.dataService.put<T>(url, data);
  }

  protected updateItem(item: any, id: any): Observable<any> {
    const data = { ...item };
    const url = this.buildUrl(id);
    return this.dataService.put<T>(url, data);
  }

  private buildUrl(id) {
    return this.url.charAt(this.url.length - 1) === '/'
      ? `${this.url}${id}`
      : `${this.url}/${id}`;
  }

  abstract update(item: T): Observable<T>;

  abstract fetch(queryString?, condition?): Observable<Result<T>>;

  abstract fetchById(id: string, condition?): Observable<T>;

  abstract createFromDTO(item): T;
}
